export function isDeliveryReviewed(invoice) {
  if (!['COG', 'The Bengal Craft'].includes(invoice.organization)) return false;
  if (invoice.deliveryReviewed !== true || !invoice.deliveryReviewedAt || !Number.isFinite(new Date(invoice.deliveryReviewedAt).getTime())) return false;
  if (!Array.isArray(invoice.items)) return false;
  const items = invoice.items;
  const ids = items.map(item => item?._id && String(item._id).toLowerCase());
  if (!ids.length || ids.some(id => !id) || new Set(ids).size !== ids.length) return false;
  const totals = new Map(ids.map(id => [id, 0]));
  const transactions = new Set(); const requests = new Set();
  if (invoice.deliveryHistory != null && !Array.isArray(invoice.deliveryHistory)) return false;
  for (const row of invoice.deliveryHistory || []) {
    const id = String(row?.itemId).toLowerCase();
    if (!totals.has(id) || !Number.isSafeInteger(row.quantity) || row.quantity <= 0 ||
        (row._id && transactions.has(String(row._id))) || (row.requestId && requests.has(row.requestId))) return false;
    if (row._id) transactions.add(String(row._id));
    if (row.requestId) requests.add(row.requestId);
    totals.set(id, totals.get(id) + row.quantity);
  }
  return items.every(item => item.deliveryVerified === true && Number.isSafeInteger(item.qty) && item.qty > 0 &&
    Number.isSafeInteger(item.deliveredQty) && item.deliveredQty >= totals.get(String(item._id).toLowerCase()) && item.deliveredQty <= item.qty);
}

// Operational classification does not alter stored review flags or quantities.
// Missing historical delivered quantities use zero only for the pending estimate.
export function itemDelivery(item = {}) {
  const ordered = Number.isSafeInteger(item?.qty) && item.qty > 0 ? item.qty : null;
  const missingDelivered = item?.deliveredQty == null;
  const delivered = missingDelivered ? 0 : Number.isSafeInteger(item.deliveredQty) && item.deliveredQty >= 0 ? item.deliveredQty : null;
  const remaining = ordered !== null && delivered !== null && delivered <= ordered ? ordered - delivered : null;
  return { ordered, delivered, remaining, uncertain: missingDelivered || remaining === null };
}
export function deliveryStatus(invoice) {
  if (!isDeliveryReviewed(invoice)) return 'Pending Delivery';
  const items = invoice.items || [];
  if (items.length && items.every(item => itemDelivery(item).remaining === 0)) return 'Fully Delivered';
  return items.some(item => (itemDelivery(item).delivered ?? 0) > 0) ? 'Partially Delivered' : 'Pending Delivery';
}
export function itemDeliveryStatus(item, reviewed) {
  const quantities = itemDelivery(item);
  if (!reviewed || quantities.remaining === null) return 'Pending Delivery';
  return quantities.remaining === 0 ? 'Fully Delivered' : quantities.delivered > 0 ? 'Partially Delivered' : 'Pending Delivery';
}

export function recordedQuantity(invoice, itemId) {
  return (invoice.deliveryHistory || []).filter(row => String(row.itemId).toLowerCase() === String(itemId).toLowerCase())
    .reduce((sum, row) => sum + Number(row.quantity), 0);
}

export function wholeQuantity(value, min, max) {
  return String(value).trim() !== '' && Number.isSafeInteger(Number(value)) && Number(value) >= min && Number(value) <= max;
}

export function localToday() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function displayDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-IN');
}

// A delivery response contains no prices/payments. Merge only delivery fields.
export function mergeDelivery(invoice, update) {
  const byId = new Map((update.items || []).filter(item => item._id).map(item => [String(item._id), item]));
  const staleItems = invoice.reportRefreshRequired || (invoice.items || []).length !== (update.items || []).length ||
    (invoice.items || []).some(item => !byId.has(String(item._id)) || (byId.get(String(item._id)).qty !== undefined && byId.get(String(item._id)).qty !== item.qty));
  return { ...invoice, reportRefreshRequired: !!staleItems, deliveryReviewed: staleItems ? false : update.deliveryReviewed, deliveryReviewedAt: update.deliveryReviewedAt,
    deliveryHistory: update.deliveryHistory,
    items: (invoice.items || []).map(item => {
      const newer = byId.get(String(item._id));
      return newer ? { ...item, deliveredQty: newer.deliveredQty, deliveryVerified: staleItems ? false : newer.deliveryVerified } : item;
    }) };
}
